import type {
  Configuration,
  ExportComplianceReportRequest,
  ListComplianceResourcesResponse,
} from '@repo/api-gw-sdk';
import { ComplianceApi } from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/compliance/';

const defaultPageSize = 500;
const maxConcurrentPages = 5;

export const compliance = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const complianceApi = new ComplianceApi(config);

  const fetchAllResources = async (
    projectId: string,
    pageSize: number
  ): Promise<ListComplianceResourcesResponse> => {
    const first = await complianceApi.listComplianceResources(
      projectId,
      0,
      pageSize
    );
    const totalCount = first.totalCount ?? first.resources?.length ?? 0;
    const firstResources = first.resources ?? [];

    if (totalCount <= pageSize) {
      return first;
    }

    const totalPages = Math.ceil(totalCount / pageSize);
    const pageIndices = Array.from({ length: totalPages - 1 }, (_, i) => i + 1);

    const allResources = [...firstResources];
    for (let i = 0; i < pageIndices.length; i += maxConcurrentPages) {
      const batch = pageIndices.slice(i, i + maxConcurrentPages);
      const results = await Promise.all(
        batch.map((page) =>
          complianceApi.listComplianceResources(projectId, page, pageSize)
        )
      );
      for (const result of results) {
        allResources.push(...(result.resources ?? []));
      }
    }

    return { resources: allResources, totalCount };
  };

  return {
    useResources: (page = 0, pageSize = defaultPageSize) =>
      httpClient.execute(
        complianceApi.listComplianceResources.bind(complianceApi),
        baseUrl,
        `resources-${page}-${pageSize}`,
        currentProjectId,
        page,
        pageSize
      ),
    useAllResources: (pageSize = defaultPageSize) =>
      httpClient.execute(
        fetchAllResources,
        baseUrl,
        `all-resources-${pageSize}`,
        currentProjectId,
        pageSize
      ),
    useTrend: (days = 90, filter?: string, enabled = true, control?: string) =>
      httpClient.execute(
        complianceApi.getComplianceTrend.bind(complianceApi),
        enabled ? baseUrl : null,
        `trend-${days}-${filter ?? ''}-${control ?? ''}`,
        currentProjectId,
        filter,
        days,
        control
      ),
    useControlTrends: (days = 90, filter?: string, enabled = true) =>
      httpClient.execute(
        complianceApi.getComplianceControlTrends.bind(complianceApi),
        enabled ? baseUrl : null,
        `control-trends-${days}-${filter ?? ''}`,
        currentProjectId,
        filter,
        days
      ),
    exportReport: (body: ExportComplianceReportRequest, filter?: string) =>
      complianceApi.exportComplianceReport(currentProjectId, body, filter),
  };
};
